/**
 * InOut — whether a record can be evaluated. A record the engine would read
 * wrong is refused, never evaluated: an impossible date, an end before its
 * start (an absence that silently counted nothing), a regime the country does
 * not know (every rule silently "not applicable"), two absences at once, more
 * than one absence still open.
 */
import { dayOf } from './dates';
import type { InOutCountryConfig, InOutRecord } from './types';

/** A record the engine refuses, with everything wrong with it. */
export class InOutRecordError extends Error {
  constructor(readonly problems: readonly string[]) {
    super(`The InOut record cannot be evaluated: ${problems.join('; ')}`);
    this.name = 'InOutRecordError';
  }
}

/**
 * The absences that share a day with the next one (by departure), in order —
 * each pair names the earlier absence first, the one in the way. An absence
 * still open runs on with no end, so any later one overlaps it. Dates that are
 * not calendar dates are left to `inoutRecordProblems`.
 */
export function inoutOverlappingAbsences<
  A extends { departedOn: string; returnedOn: string | null },
>(absences: readonly A[]): Array<[A, A]> {
  const spans = absences.flatMap((absence) => {
    try {
      const start = dayOf(absence.departedOn);
      const end = absence.returnedOn === null ? null : dayOf(absence.returnedOn);
      return [{ absence, start, end }];
    } catch {
      return [];
    }
  });
  spans.sort((a, b) => a.start - b.start);
  const pairs: Array<[A, A]> = [];
  for (let i = 1; i < spans.length; i++) {
    const before = spans[i - 1];
    if (before.end === null || before.end >= spans[i].start) {
      pairs.push([before.absence, spans[i].absence]);
    }
  }
  return pairs;
}

/**
 * The absence a trip touches — they share only a border day, one starting the
 * day the other ends (owner, 22 Oct 2026: out to Spain in the morning, back for
 * lunch, out again in the afternoon) — or null. Days are whole: the law counts
 * them (A2 — the departure and the return day are both away), so the shared
 * day counts once whichever trip holds it, and the two can be one trip without
 * changing a number. Sharing more than that day is an overlap, never a touch;
 * an absence still open touches nothing after it.
 */
export function inoutTouchingAbsence<A extends { departedOn: string; returnedOn: string | null }>(
  trip: { departedOn: string; returnedOn: string | null },
  absences: readonly A[]
): A | null {
  const start = dayOf(trip.departedOn);
  const end = trip.returnedOn === null ? Number.POSITIVE_INFINITY : dayOf(trip.returnedOn);
  return (
    absences.find((other) => {
      const otherStart = dayOf(other.departedOn);
      const otherEnd =
        other.returnedOn === null ? Number.POSITIVE_INFINITY : dayOf(other.returnedOn);
      // The days both hold: exactly one, the end of one and the start of the other.
      const from = Math.max(start, otherStart);
      const to = Math.min(end, otherEnd);
      return (
        from === to &&
        ((from === otherEnd && from === start) || (from === end && from === otherStart))
      );
    }) ?? null
  );
}

/** Two trips that touch, as one: the first departure, the last return — open if either is. */
export function inoutJoinedTrip(
  a: { departedOn: string; returnedOn: string | null },
  b: { departedOn: string; returnedOn: string | null }
): { departedOn: string; returnedOn: string | null } {
  return {
    departedOn: a.departedOn <= b.departedOn ? a.departedOn : b.departedOn,
    returnedOn:
      a.returnedOn === null || b.returnedOn === null
        ? null
        : a.returnedOn >= b.returnedOn
          ? a.returnedOn
          : b.returnedOn,
  };
}

/** Everything that makes a record unreadable for this country — empty when none. */
export function inoutRecordProblems(config: InOutCountryConfig, record: InOutRecord): string[] {
  const problems: string[] = [];
  const day = (value: string, where: string): number | null => {
    try {
      return dayOf(value);
    } catch {
      problems.push(`${where}: ${value} is not a calendar date`);
      return null;
    }
  };
  const ordered = (from: string, to: string | null, where: string) => {
    const start = day(from, where);
    const end = to === null ? null : day(to, where);
    if (start !== null && end !== null && end < start) {
      problems.push(`${where}: ends (${to}) before it starts (${from})`);
    }
    return { start, end };
  };

  const regimes = new Map(config.regimes.map((r) => [r.id, r]));
  const titleIds = new Set(record.titles.map((t) => t.id));
  for (const title of record.titles) {
    const where = `title ${title.id}`;
    ordered(title.issuedOn, title.validUntil, where);
    const regime = regimes.get(title.regime);
    if (!regime) {
      problems.push(`${where}: regime ${title.regime} is not one of ${config.code}'s`);
    } else if (title.subtype && !regime.subtypes.some((s) => s.id === title.subtype)) {
      problems.push(`${where}: subtype ${title.subtype} is not a subtype of ${title.regime}`);
    }
    if (title.previousTitleId && !titleIds.has(title.previousTitleId)) {
      problems.push(
        `${where}: the title it renews (${title.previousTitleId}) is not in the record`
      );
    }
  }

  for (const absence of record.absences) {
    ordered(absence.departedOn, absence.returnedOn, `absence ${absence.id}`);
  }
  if (record.absences.filter((a) => a.returnedOn === null).length > 1) {
    problems.push('more than one absence is still open');
  }
  for (const [before, after] of inoutOverlappingAbsences(record.absences)) {
    problems.push(`absences ${before.id} and ${after.id} overlap`);
  }

  for (const trip of record.plannedTrips)
    ordered(trip.departsOn, trip.returnsOn, `planned trip ${trip.id}`);
  record.confirmations.forEach((c, i) =>
    ordered(c.periodStart, c.periodEnd, `confirmation ${i + 1}`)
  );
  return problems;
}
